/**
 * SparkleIndicator Component
 *
 * Visual indicator (✨) for values that have changed due to delta application.
 * Features:
 * - Uses Tabler IconSparkles
 * - CSS pulse-glow animation (2s duration)
 * - Auto-removes after 2 seconds (unless persistent)
 * - Uses sparkle virtual color token (amber/gold)
 * - Static display when prefers-reduced-motion enabled
 *
 * @see Story 2.1: Delta Engine Wired to Q&A; Epic Story 2.7: AC closure (timing with AnimatedValue at call sites); Epic Story 2.11: same timing contract with P1–P3 stagger.
 */

import { useEffect, useRef } from 'react'
import { Box } from '@mantine/core'
import { useReducedMotion } from '@mantine/hooks'
import { IconSparkles } from '@tabler/icons-react'
import styles from './SparkleIndicator.module.css'

export interface SparkleIndicatorProps {
  /** Whether the sparkle is visible */
  visible: boolean
  /** Callback when sparkle should be removed (after auto-remove timeout) */
  onRemove?: () => void
  /** Prevent auto-removal (sparkle stays until manually hidden) */
  persistent?: boolean
  /** Size variant */
  size?: 'sm' | 'md'
  /** Custom aria-label */
  ariaLabel?: string
}

const SIZE_MAP = {
  sm: 14,
  md: 16,
} as const

const AUTO_REMOVE_DELAY = 2000

export function SparkleIndicator({
  visible,
  onRemove,
  persistent = false,
  size = 'sm',
  ariaLabel = 'Value changed',
}: SparkleIndicatorProps) {
  const onRemoveRef = useRef(onRemove)
  onRemoveRef.current = onRemove
  const hasOnRemove = onRemove != null
  const prefersReducedMotion = useReducedMotion(false, { getInitialValueInEffect: false })

  useEffect(() => {
    if (!visible || persistent || !hasOnRemove) {
      return
    }
    const id = setTimeout(() => {
      onRemoveRef.current?.()
    }, AUTO_REMOVE_DELAY)

    return () => {
      clearTimeout(id)
    }
  }, [visible, persistent, hasOnRemove])

  if (!visible) {
    return null
  }

  const iconSize = SIZE_MAP[size]
  const animationClass = prefersReducedMotion ? styles.static : styles.pulse

  return (
    <Box
      component="span"
      className={`${styles.wrapper} ${animationClass}`}
      aria-label={ariaLabel}
      role="img"
    >
      <IconSparkles
        size={iconSize}
        style={{ color: 'var(--mantine-color-sparkle-5)' }}
        aria-hidden="true"
      />
    </Box>
  )
}
